"use client"

import { Navbar } from "@/components/navbar"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { getAuthFailureMessage } from "@/lib/auth/authErrors"
import { buildOAuthRedirectTo } from "@/lib/auth/oauthRedirect"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"

type LoginScreenProps = {
  authError?: string | null
  authReason?: string | null
  next?: string | null
  withNavbar?: boolean
}

export function LoginScreen({
  authError,
  authReason,
  next,
  withNavbar = true,
}: LoginScreenProps) {
  const [loading, setLoading] = useState(false)
  const supabase = createClient()
  const authMessage = getAuthFailureMessage({
    error: authError ?? null,
    reason: authReason ?? null,
  })

  const handleGoogleSignIn = async () => {
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: buildOAuthRedirectTo({
            origin: window.location.origin,
            next: next ?? undefined,
          }),
        },
      })

      if (error) {
        console.error("signInWithGoogle failed", error)
      }
    } catch (error) {
      console.error("signInWithGoogle threw", error)
    } finally {
      setLoading(false)
    }
  }

  const content = (
    <div className="relative flex min-h-[calc(100vh-40px)] items-center justify-center bg-background px-4 py-8">
      <div className="relative w-full max-w-sm">
        <div className="mb-6 text-center">
          <Link href="/" className="inline-flex flex-col items-center gap-3">
            <Image src="/logo.gif" alt="Ego logo" width={42} height={42} />
            <span className="text-[11px] font-medium tracking-[0.28em] uppercase">
              Ego World
            </span>
          </Link>
        </div>

        <Card
          variant="content"
          className="rounded-[24px] border-white/10 bg-black/55 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.42)] backdrop-blur-2xl"
        >
          <div className="mb-6 text-center">
            {/* <span className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] tracking-[0.22em] text-muted-foreground uppercase">
              Sign in
            </span> */}
            <h1 className="mb-2 text-[28px] font-semibold tracking-tight text-foreground">
              Welcome
            </h1>
            <p className="mx-auto max-w-[22rem] text-[13px] leading-6 text-muted-foreground">
              Sign in to manage your characters and skills.
            </p>
          </div>

          {authMessage ? (
            <div className="mb-5 rounded-2xl border border-primary/20 bg-primary/8 px-4 py-3 text-center text-sm text-foreground">
              {authMessage}
            </div>
          ) : null}

          <Button
            className="h-12 w-full gap-3 rounded-2xl border border-white/10 bg-white/5 text-sm text-foreground hover:border-white/15 hover:bg-white/8 hover:text-foreground"
            size="lg"
            variant="secondary"
            onClick={handleGoogleSignIn}
            loading={loading}
          >
            {!loading && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            Continue with Google
          </Button>

          <p className="mt-4 text-center text-[11px] leading-5 text-muted-foreground">
            By signing in, you agree to our terms of service.
          </p>
        </Card>

        {/* <p className="mt-6 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <button
            onClick={handleGoogleSignIn}
            className="font-medium text-foreground transition-colors hover:text-foreground/80"
          >
            Sign up for free
          </button>
        </p> */}
      </div>
    </div>
  )

  if (!withNavbar) {
    return content
  }

  return (
    <>
      <Navbar />
      <div className="min-h-[calc(100vh-5rem)] md:min-h-[calc(100vh-6rem)]">
        {content}
      </div>
    </>
  )
}
